import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listEvents, listPatients, listRooms } from '../lib/adapters';
import { useI18n } from '../contexts/I18nContext';
import { SeverityDot } from '../components/StatusChip';
import PaginationFooter, { headerInt } from '../components/PaginationFooter';
import { useRelativeTime, formatDateTime } from '../lib/time';
import { api } from '../lib/api';
import { eventIcon } from '../lib/eventVisual';
import { MagnifyingGlass, FunnelSimple } from '@phosphor-icons/react';

const TYPE_FILTERS = ['all', 'fall', 'sos', 'door_open', 'low_battery', 'offline'];
const SEVERITY_FILTERS = ['all', 'info', 'warning', 'critical'];

export default function Events() {
  const { t, lang } = useI18n();
  const rel = useRelativeTime();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState('all');
  const [severity, setSeverity] = useState('all');
  const [roomId, setRoomId] = useState(searchParams.get('room') || 'all');
  const [patientId, setPatientId] = useState('all');
  const [q, setQ] = useState('');
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [patients, setPatients] = useState([]);
  const PAGE_SIZE = 200;

  useEffect(() => {
    listPatients().then(({ items }) => setPatients(items)).catch(() => setPatients([]));
    listRooms().then(({ items }) => setRooms(items)).catch(() => setRooms([]));
  }, []);

  useEffect(() => {
    const typeParam = filter === 'all' ? undefined : filter;
    listEvents({ type: typeParam, limit: PAGE_SIZE, offset: 0 })
      .then(({ items, total: tot }) => {
        setEvents(items);
        setTotal(tot);
      })
      .catch(() => { setEvents([]); setTotal(0); });
  }, [filter]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.get('/events', {
        params: {
          ...(filter !== 'all' ? { type: filter } : {}),
          limit: PAGE_SIZE,
          offset: events.length,
        },
      });
      const more = Array.isArray(r.data) ? r.data : [];
      setEvents((prev) => [...prev, ...more]);
      const next = headerInt(r, 'X-Total-Count');
      if (next != null) setTotal(next);
    } finally {
      setLoadingMore(false);
    }
  };

  // Map patient_id -> room_id once for fast filtering.
  const patientToRoom = useMemo(() => {
    const m = {};
    patients.forEach((p) => { m[p.id] = p.room_id; });
    return m;
  }, [patients]);

  const filtered = events.filter((e) => {
    if (e.type === 'heartbeat') return false;
    if (severity !== 'all' && e.severity !== severity) return false;
    if (patientId !== 'all' && e.resident_id !== patientId) return false;
    if (roomId !== 'all' && patientToRoom[e.resident_id] !== roomId) return false;
    if (q) {
      const needle = q.toLowerCase();
      const hit = (e.resident_name || '').toLowerCase().includes(needle)
        || (e.message || '').toLowerCase().includes(needle);
      if (!hit) return false;
    }
    return true;
  });

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-sage-500 font-semibold mb-3">
          {t('nav_events')}
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl text-ink leading-none">{t('events_title')}</h1>
        <p className="text-ink-soft mt-3 max-w-xl">{t('events_sub')}</p>
      </header>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted" />
            <input
              type="text"
              placeholder={t('search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="events-search"
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100"
            />
          </div>

          <FilterSelect
            label={t('events_filter_severity')}
            value={severity}
            onChange={setSeverity}
            options={SEVERITY_FILTERS.map((s) => ({ value: s, label: s === 'all' ? t('all') : t(`severity_${s}`) }))}
            testid="events-filter-severity"
          />
          <FilterSelect
            label={t('events_filter_room')}
            value={roomId}
            onChange={setRoomId}
            options={[{ value: 'all', label: t('all') }, ...rooms.map((r) => ({ value: r.id, label: r.room_name }))]}
            testid="events-filter-room"
          />
          <FilterSelect
            label={t('events_filter_patient')}
            value={patientId}
            onChange={setPatientId}
            options={[{ value: 'all', label: t('all') }, ...patients.map((p) => ({ value: p.id, label: p.name }))]}
            testid="events-filter-patient"
          />
        </div>

        <div className="flex flex-wrap gap-2" data-testid="events-type-filters">
          {TYPE_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`events-filter-${f}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                filter === f
                  ? 'bg-ink text-white border-ink'
                  : 'bg-white text-ink-soft border-stone_line hover:bg-bg-alt'
              }`}
            >
              {f === 'all' ? t('all') : t(`event_${f}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-stone_line rounded-2xl shadow-soft overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-ink-muted">{t('no_results')}</div>
        ) : (
          <ul className="divide-y divide-stone_line" data-testid="events-list">
            {filtered.map((ev) => {
              const Icon = eventIcon(ev.type);
              return (
                <li key={ev.id} className="flex items-center gap-4 px-6 py-4 hover:bg-bg-alt/50 transition-colors">
                  <div className="w-9 h-9 rounded-lg bg-bg-alt grid place-items-center text-ink-soft">
                    <Icon size={18} weight="duotone" />
                  </div>
                  <SeverityDot severity={ev.severity} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3">
                      <div className="font-medium text-ink truncate text-sm">{t(`event_${ev.type}`)}</div>
                      <span className="text-xs text-ink-muted truncate">{ev.resident_name}</span>
                    </div>
                    {ev.message && <div className="text-xs text-ink-soft truncate mt-0.5">{ev.message}</div>}
                  </div>
                  <div className="hidden sm:block text-right">
                    <div className="text-xs text-ink font-medium">{formatDateTime(ev.created_at, lang)}</div>
                    <div className="text-xs text-ink-muted">{rel(ev.created_at)}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <PaginationFooter
          shown={events.length}
          total={total}
          onLoadMore={loadMore}
          loading={loadingMore}
          testid="events-pagination"
        />
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-2 bg-white border border-stone_line rounded-xl px-3 py-2">
      <FunnelSimple size={14} className="text-ink-muted" />
      <span className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-semibold">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="bg-transparent text-sm text-ink focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
